import React, { useMemo, useState } from 'react';
import { Segmented, Skeleton } from 'antd';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { useDashboardKpi } from '@/api/dashboard';
import { useWeatherCities } from '@/api/weather';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTodos, type TodoItem } from '@/api/todos';
import { format2 } from '@/utils/charts';
import type { EChartsOption } from 'echarts';
import type { StationKpi, StationBreakdown } from '@/types/api';

// ── design tokens ──
const PLAN_COLOR = '#2563eb';
const T = '#0d9488';           // teal primary
const T2 = '#2563eb';          // blue accent
const T3 = '#7c3aed';          // purple accent
const DARK = '#1a2b4a';
const MUTED = '#6b7280';
const GRID = '#f0f2f5';
const CARD_SHADOW = '0 2px 12px rgba(0,0,0,0.08)';
const CARD_RADIUS = 12;

// ── mock data (from HTML prototype) ──
const MOCK_NOTICES = [
  { tag: '政策', cls: 'zc', title: '关于完善新能源上网电价市场化形成机制的通知', date: '2026-05-01' },
  { tag: '紧急', cls: 'jj', title: '端午节期间电网调峰安排及注意事项', date: '2026-05-05' },
  { tag: '通知', cls: 'tz', title: '5月月度交易申报时间调整至5月12日', date: '2026-05-08' },
  { tag: '公告', cls: 'gg', title: '储能电站AGC性能测试通过公示', date: '2026-05-10' },
  { tag: '重要', cls: 'zy', title: '省电力交易中心发布现货市场试运行公告', date: '2026-05-11' },
];

const TAG_STYLE: Record<string, React.CSSProperties> = {
  zc: { background: '#E3F2FD', color: '#0d9488' },
  jj: { background: '#FFEBEE', color: '#C62828' },
  tz: { background: '#FFF3E0', color: '#E65100' },
  gg: { background: '#E8F5E9', color: '#2E7D32' },
  zy: { background: '#FFF8E1', color: '#F57F17' },
};

const MOCK_TASKS = [
  { name: '5月月度交易申报', initiator: '吴建华', owner: '王明', status: 'ing' as const, deadline: '2026-05-15' },
  { name: 'A区逆变器年度检修', initiator: '赵志强', owner: '孙涛', status: 'wait' as const, deadline: '2026-05-20' },
  { name: '储能电站容量测试', initiator: '刘建国', owner: '周磊', status: 'done' as const, deadline: '2026-05-10' },
  { name: '现货市场策略优化', initiator: '黄伟明', owner: '张伟', status: 'delay' as const, deadline: '2026-05-18' },
  { name: '功率预测模型校准', initiator: '郑晓峰', owner: '李强', status: 'ing' as const, deadline: '2026-05-22' },
  { name: 'Q2财务报表编制', initiator: '陈静', owner: '陈总', status: 'wait' as const, deadline: '2026-05-30' },
  { name: '新场站并网验收', initiator: '林晓东', owner: '赵刚', status: 'ing' as const, deadline: '2026-05-25' },
  { name: '设备巡检计划制定', initiator: '赵志强', owner: '孙涛', status: 'done' as const, deadline: '2026-05-08' },
  { name: '年度安全生产检查', initiator: '马文斌', owner: '周磊', status: 'wait' as const, deadline: '2026-06-05' },
  { name: '电价预测算法升级', initiator: '杨帆', owner: '李强', status: 'done' as const, deadline: '2026-05-12' },
];

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  ing: { label: '进行中', cls: 'status-ing' },
  wait: { label: '待处理', cls: 'status-wait' },
  done: { label: '已完成', cls: 'status-done' },
  delay: { label: '已延期', cls: 'status-delay' },
};

type CalendarEvents = Record<string, { time: string; text: string; tag: string; tagText: string }[]>;

// ── period config ──
const PERIODS = [
  { label: '昨日', value: 'day' },
  { label: '本周', value: 'week' },
  { label: '本月', value: 'month' },
  { label: '本年', value: 'year' },
];

// ── helpers ──
function fmEnergy(v: number): string {
  return v.toFixed(1);
}

// ── mini chart factory ──
function lineOpt(labels: string[], series: { name: string; data: number[]; color?: string; area?: boolean }[], extra?: Partial<EChartsOption>): EChartsOption {
  const hasLegend = series.length > 1;
  return {
    tooltip: { trigger: 'axis' as const, textStyle: { fontSize: 11 } },
    legend: hasLegend ? { top: 0, right: 0, textStyle: { fontSize: 10, color: MUTED }, itemWidth: 12, itemHeight: 6 } : undefined,
    grid: { left: 42, right: 10, top: hasLegend ? 22 : 10, bottom: 22 },
    xAxis: {
      type: 'category' as const, data: labels,
      axisLabel: { fontSize: 9, color: '#888', interval: labels.length > 20 ? Math.floor(labels.length / 8) : 0 },
      axisLine: { lineStyle: { color: '#E2E8F0' } },
    },
    yAxis: {
      type: 'value' as const, nameTextStyle: { fontSize: 10, color: '#888' },
      axisLabel: { fontSize: 9, color: '#888' },
      splitLine: { lineStyle: { color: GRID } },
      splitNumber: 3,
    },
    series: series.map((s) => ({
      name: s.name, type: 'line' as const, data: s.data, smooth: true,
      symbol: 'none' as const,
      lineStyle: { width: 1.5, color: s.color },
      areaStyle: s.area ? { color: { type: 'linear' as const, x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: (s.color || '#0d9488') + '20' }, { offset: 1, color: (s.color || '#0d9488') + '03' }] } } : undefined,
    })),
    ...extra,
  };
}

// ── calendar widget ──
const CalendarWidget: React.FC<{ events: CalendarEvents }> = ({ events }) => {
  const now = dayjs();
  const [year, setYear] = useState(now.year());
  const [month, setMonth] = useState(now.month() + 1);
  const [selected, setSelected] = useState(now.format('YYYY-MM-DD'));

  const firstDay = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).day();
  const daysInMonth = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).daysInMonth();
  const prevDays = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).subtract(1, 'day').date();

  const today = now.format('YYYY-MM-DD');
  const weeks = ['日', '一', '二', '三', '四', '五', '六'];

  const prevMonth = () => { if (month === 1) { setYear(y => y - 1); setMonth(12); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setYear(y => y + 1); setMonth(1); } else setMonth(m => m + 1); };

  const selEvents = events[selected] || [];

  return (
    <div style={{ display: 'flex', gap: 8, flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1.1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{year}年{month}月</span>
          <div style={{ display: 'flex', gap: 2 }}>
            <button onClick={prevMonth} style={calBtnStyle}>◀</button>
            <button onClick={nextMonth} style={calBtnStyle}>▶</button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, textAlign: 'center', fontSize: 12 }}>
          {weeks.map(w => <div key={w} style={{ color: '#999', fontWeight: 600, padding: '2px 0' }}>{w}</div>)}
          {Array.from({ length: firstDay }, (_, i) => (
            <div key={`p${i}`} style={{ padding: '3px 0', color: '#ccc', borderRadius: 4 }}>{prevDays - firstDay + i + 1}</div>
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const d = i + 1;
            const ds = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const isToday = ds === today;
            const isSel = ds === selected;
            const hasEvent = !!events[ds];
            return (
              <div
                key={d}
                onClick={() => setSelected(ds)}
                style={{
                  padding: '3px 0', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                  background: isToday ? T : isSel ? '#B2DFDB' : 'transparent',
                  color: isToday ? '#fff' : isSel ? T : '#333',
                  fontWeight: (isToday || isSel) ? 600 : 400,
                  position: 'relative' as const,
                }}
              >
                {d}
                {hasEvent && (
                  <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 3, height: 3, borderRadius: '50%', background: events[ds][0].tag === 'jj' ? '#E63946' : events[ds][0].tag === 'zy' ? '#F4A261' : T }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ flex: 0.9, minWidth: 0, overflow: 'auto' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 4 }}>
          {selected === today ? '今日事件' : `${selected} 事件`}
        </div>
        {selEvents.length === 0 ? (
          <div style={{ fontSize: 13, color: '#999' }}>暂无事件</div>
        ) : selEvents.map((e, i) => (
          <div key={i} style={{ fontSize: 12, padding: '3px 0', borderBottom: '1px dashed #F2F3F5', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
            <span style={{ ...TAG_STYLE[e.tag] || {}, display: 'inline-block', padding: '1px 5px', borderRadius: 3, fontSize: 10, marginRight: 3 }}>{e.tagText}</span>
            <span style={{ color: T, fontWeight: 600, whiteSpace: 'nowrap' }}>{e.time}</span>
            <span style={{ color: '#333' }}>{e.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const calBtnStyle: React.CSSProperties = {
  background: 'transparent', color: '#666', border: '1px solid #E2E8F0', borderRadius: 4,
  padding: '1px 6px', cursor: 'pointer', fontSize: 12,
};

// ── KPI card ──
function KpiCard({ icon, iconBg, label, value, unit, sub }: { icon: string; iconBg: string; label: string; value: string; unit: string; sub: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', borderRadius: CARD_RADIUS, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: CARD_SHADOW, padding: '10px 12px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 2, whiteSpace: 'nowrap' }}>{label}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: DARK, whiteSpace: 'nowrap', lineHeight: 1 }}>
            {value}<span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 2 }}>{unit}</span>
          </div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{sub}</div>
        </div>
      </div>
    </div>
  );
}

// ── main page ──
const DashboardPage: React.FC = () => {
  const [period, setPeriod] = useState('day');
  const isMobile = useIsMobile();
  const today = dayjs().format('YYYY-MM-DD');

  const { data, isLoading, isFetching } = useDashboardKpi({ period: period as 'day' | 'week' | 'month' | 'year', date: today });
  const { data: weatherData } = useWeatherCities();
  const { data: todoData } = useTodos();

  const kpiData = data?.data;
  const weatherCities = weatherData?.cities ?? [];
  const monthlyPlan = kpiData?.monthly_plan ?? null;
  const allTodos: TodoItem[] = (todoData?.data?.todos ?? []) as TodoItem[];
  const todayTodos = allTodos.filter(t => t.is_daily || t.due_date === dayjs().format('YYYY-MM-DD'));

  // Build calendar events from real todos (those with due_date)
  const calendarEvents: CalendarEvents = {};
  for (const t of allTodos) {
    if (!t.due_date) continue;
    const tag = t.priority === 'urgent' ? 'jj' : t.priority === 'important' ? 'zy' : 'tz';
    const tagText = t.priority === 'urgent' ? '紧急' : t.priority === 'important' ? '重要' : '通知';
    if (!calendarEvents[t.due_date]) calendarEvents[t.due_date] = [];
    calendarEvents[t.due_date].push({ time: t.due_time || '', text: t.title, tag, tagText });
  }

  // Countdown ticker
  const [tick, setTick] = useState(0);
  React.useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const getCountdown = (timeStr: string): { over: boolean; text: string } => {
    if (!timeStr) return { over: false, text: '' };
    const [h, m] = timeStr.split(':').map(Number);
    const target = dayjs().hour(h).minute(m).second(0);
    const diff = target.diff(dayjs(), 'second');
    if (diff <= 0) return { over: true, text: timeStr };
    const hh = String(Math.floor(diff / 3600)).padStart(2, '0');
    const mm = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
    const ss = String(diff % 60).padStart(2, '0');
    return { over: false, text: `${hh}:${mm}:${ss}` };
  };
  void tick; // consume tick to force re-render
  const stations: StationKpi[] = kpiData?.stations ?? [];
  const breakdown: StationBreakdown[] = kpiData?.breakdown ?? [];
  const provincePrices = kpiData?.province_prices ?? [];

  const totalKpi = useMemo<StationKpi | null>(() => {
    if (stations.length === 0) return null;
    const t: StationKpi = {
      name: '合计', energy_mwh: 0, revenue_yuan: 0, avg_price: 0,
      gen_energy_mwh: 0, grid_energy_mwh: 0, curtailment_rate: 0,
      subsidy_revenue: 0, spot_revenue: 0, storage_spot_revenue: 0, storage_subsidy_revenue: 0,
      penalty_avoided: 0, virtual_recovery: 0,
      pv_energy_mwh: 0, bat_discharge_mwh: 0, bat_charge_mwh: 0,
      energy_change_pct: null, revenue_change_pct: null, price_change_pct: null,
    };
    for (const s of stations) {
      t.energy_mwh += s.energy_mwh; t.revenue_yuan += s.revenue_yuan;
      t.gen_energy_mwh += s.gen_energy_mwh; t.grid_energy_mwh += s.grid_energy_mwh;
      t.subsidy_revenue += s.subsidy_revenue; t.spot_revenue += s.spot_revenue;
      t.storage_spot_revenue += s.storage_spot_revenue; t.storage_subsidy_revenue += s.storage_subsidy_revenue;
      t.penalty_avoided += s.penalty_avoided; t.virtual_recovery += s.virtual_recovery;
      t.pv_energy_mwh += s.pv_energy_mwh; t.bat_discharge_mwh += s.bat_discharge_mwh;
      t.bat_charge_mwh += s.bat_charge_mwh;
    }
    t.curtailment_rate = t.gen_energy_mwh > 0 ? (t.gen_energy_mwh - t.grid_energy_mwh) / t.gen_energy_mwh * 100 : 0;
    t.avg_price = t.energy_mwh > 0 ? t.revenue_yuan / t.energy_mwh : 0;
    return t;
  }, [stations]);

  // ── chart data ──
  const breakdownLabels = breakdown.length > 0 ? breakdown[0].labels : [];

  // Layer 1 revenue trend (monthly, from breakdown or all stations)
  const revenueChartOption: EChartsOption = useMemo(() => {
    const series = breakdown.map((bd, i) => ({
      name: bd.name, data: bd.revenue, color: [T, T2, T3][i % 3], area: true,
    }));
    return lineOpt(breakdownLabels, series);
  }, [breakdownLabels, breakdown]);

  // Layer 4 chart 1: 上网电量趋势 - 按场站
  const powerGridOption: EChartsOption = useMemo(() => {
    const series = breakdown.map((bd, i) => ({
      name: bd.name,
      data: bd.energy,
      color: [T, '#00A878', T2][i % 3],
      area: true,
    }));
    return lineOpt(breakdownLabels, series, { yAxis: [{ type: 'value', name: 'MWh' }] });
  }, [breakdownLabels, breakdown]);

  // Layer 4 chart 2: 日收益趋势 - 按场站 (stacked bar)
  const revenueBarOption: EChartsOption = useMemo(() => {
    return {
      tooltip: { trigger: 'axis' as const, textStyle: { fontSize: 11 } },
      legend: { top: 0, textStyle: { fontSize: 10, color: MUTED }, itemWidth: 12, itemHeight: 6 },
      grid: { left: 42, right: 10, top: 22, bottom: 22 },
      xAxis: { type: 'category' as const, data: breakdownLabels, axisLabel: { fontSize: 9, color: '#888' } },
      yAxis: { type: 'value' as const, name: '万元', axisLabel: { fontSize: 9, color: '#888' }, splitLine: { lineStyle: { color: GRID } } },
      series: breakdown.map((bd, i) => ({
        name: bd.name, type: 'bar' as const, stack: 'total',
        data: bd.revenue.map((v: number) => +(v / 10000).toFixed(2)),
        itemStyle: { color: [T, '#00A878'][i] || T, borderRadius: i === 0 ? [2, 2, 0, 0] : [0, 0, 2, 2] },
        barWidth: 14,
      })),
    };
  }, [breakdownLabels, breakdown]);

  // Layer 4 chart 3: 日前 vs 实时电价曲线
  const priceCurveOption: EChartsOption = useMemo(() => {
    const times = provincePrices.map(p => p.time);
    const daVals = provincePrices.map(p => p.da);
    const rtVals = provincePrices.map(p => p.rt);
    return {
      tooltip: { trigger: 'axis' as const, textStyle: { fontSize: 11 }, valueFormatter: (v: unknown) => typeof v === 'number' ? format2(v) + ' 元/MWh' : String(v ?? '') },
      legend: { data: ['日前出清', '实时出清'], top: 0, textStyle: { fontSize: 10, color: MUTED }, itemWidth: 12, itemHeight: 6 },
      grid: { left: 45, right: 10, top: 26, bottom: 22 },
      xAxis: { type: 'category' as const, data: times, axisLabel: { fontSize: 9, color: '#888', interval: times.length > 20 ? Math.floor(times.length / 8) : 0 }, axisLine: { lineStyle: { color: '#E2E8F0' } } },
      yAxis: { type: 'value' as const, name: '元/MWh', axisLabel: { fontSize: 9, color: '#888' }, splitLine: { lineStyle: { color: GRID } } },
      series: [
        { name: '日前出清', type: 'line', data: daVals, smooth: true, symbol: 'none', lineStyle: { color: '#9ca3af', width: 1.5, type: 'dashed' } },
        { name: '实时出清', type: 'line', data: rtVals, smooth: true, symbol: 'none', lineStyle: { color: T, width: 2 }, connectNulls: false },
      ],
    };
  }, [provincePrices]);

  // ── task filter ──
  const [taskFilter, setTaskFilter] = useState('all');
  const [mobileRightTab, setMobileRightTab] = useState('notices');
  const statusCounts = useMemo(() => {
    const c: Record<string, number> = { all: MOCK_TASKS.length };
    MOCK_TASKS.forEach(t => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, []);
  const filteredTasks = taskFilter === 'all' ? MOCK_TASKS : MOCK_TASKS.filter(t => t.status === taskFilter);

  // ── loading ──
  if (isLoading && !kpiData) {
    return (
      <div style={{ padding: 40, height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: DARK, marginBottom: 4 }}>正在加载首页数据</div>
          <div style={{ fontSize: 12, color: MUTED }}>首次加载需查询数据库，请稍候...</div>
        </div>
        <div style={{ width: '60%', maxWidth: 520 }}>
          <Skeleton active paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 2 }} style={{ marginTop: 12 }} />
        </div>
      </div>
    );
  }

  if (!kpiData) {
    return <div style={{ padding: 40, textAlign: 'center', color: MUTED, paddingTop: 120 }}>暂无数据</div>;
  }

  const totalRev = totalKpi?.revenue_yuan ?? 0;
  const totalEnergy = totalKpi?.energy_mwh ?? 0;
  const totalPrice = totalKpi?.avg_price ?? 0;
  const totalPvEnergy = totalKpi?.pv_energy_mwh ?? 0;

  // ── mobile layout ──
  if (isMobile) {
    return (
      <div style={{ padding: '8px 12px', minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 8, background: '#F5F7FA', overflowY: 'auto' }}>
        {/* Time bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', borderRadius: 12, padding: '5px 12px', boxShadow: CARD_SHADOW, height: 36, flexShrink: 0 }}>
          <span style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>时间范围</span>
          <Segmented size="small" value={period} onChange={(v) => setPeriod(v as string)} options={PERIODS} />
          {weatherCities.length > 0 && (
            <div style={{ height: 20, overflow: 'hidden', fontSize: 11, color: MUTED, lineHeight: '20px', whiteSpace: 'nowrap', flex: 1 }}>
              <div style={{ animation: `weatherCityScroll ${weatherCities.length * 4}s linear infinite` }}>
                {weatherCities.map((c, i) => <div key={i} style={{ height: 20 }}>{c.city_name} 风速 {c.wind_speed} km/h</div>)}
                {weatherCities.map((c, i) => <div key={`dup-${i}`} style={{ height: 20 }}>{c.city_name} 风速 {c.wind_speed} km/h</div>)}
              </div>
            </div>
          )}
          <span style={{ fontSize: 12, color: MUTED, whiteSpace: 'nowrap' }}>
            {kpiData.date_start} ~ {kpiData.date_end}
          </span>
        </div>

        {/* Layer 1: Revenue left + Chart right */}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '10px 12px', boxShadow: CARD_SHADOW }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>💰</div>
              <span style={{ fontSize: 14, color: '#333', fontWeight: 600 }}>收益（{PERIODS.find(p => p.value === period)?.label || ''}）</span>
            </div>
            <div style={{ fontSize: 36, fontWeight: 700, color: DARK, lineHeight: 1 }}>
              {format2(totalRev)}<span style={{ fontSize: 14, fontWeight: 400, color: MUTED, marginLeft: 4 }}>元</span>
            </div>
            <div style={{ fontSize: 11, color: '#999', lineHeight: 1.6 }}>
              上网 {fmEnergy(totalEnergy)} MWh · 光伏 {fmEnergy(totalPvEnergy)} MWh · 均价 {format2(totalPrice)} 元/MWh
            </div>
            {totalKpi?.revenue_change_pct != null && (
              <span style={{ fontSize: 12, color: totalKpi.revenue_change_pct >= 0 ? '#00A878' : '#E63946', fontWeight: 600 }}>
                环比 {totalKpi.revenue_change_pct >= 0 ? '+' : ''}{totalKpi.revenue_change_pct}%
              </span>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '8px 10px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: MUTED, marginBottom: 2 }}>收益趋势</div>
            <div style={{ flex: 1, minHeight: 0 }}>
              {breakdown.length > 0 ? <ReactECharts option={revenueChartOption} style={{ height: '100%' }} /> : <div style={{ color: '#999', textAlign: 'center', paddingTop: '15%' }}>暂无数据</div>}
            </div>
          </div>
        </div>

        {/* Today's todos - standalone row */}
        {todayTodos.length > 0 && (
          <div style={{ background: '#fff', borderRadius: CARD_RADIUS, padding: '8px 12px', boxShadow: CARD_SHADOW }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#E63946', marginBottom: 4 }}>今日待办</div>
            {todayTodos.slice(0, 5).map((t, i) => {
              const pr = t.priority || 'normal';
              const tagBg = pr === 'urgent' ? '#FFEBEE' : pr === 'important' ? '#FFF8E1' : '#F5F5F5';
              const tagColor = pr === 'urgent' ? '#C62828' : pr === 'important' ? '#F57F17' : '#666';
              const cd = getCountdown(t.due_time || '');
              return (
                <div key={t.id || i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', fontSize: 11 }}>
                  <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: tagBg, color: tagColor }}>{pr === 'urgent' ? '紧急' : pr === 'important' ? '重要' : '普通'}</span>
                  <span style={{ flex: 1, color: '#333', textDecoration: cd.over ? 'line-through' : 'none' }}>{t.title}</span>
                  {t.due_time && <span style={{ color: cd.over ? '#999' : '#E63946', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>{cd.over ? t.due_time : cd.text}</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Layer 2: KPI cards - 3 per row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          <KpiCard icon="⚡" iconBg="#E3F2FD" label="总装机容量" value="252" unit="MW" sub="光伏 250MW / 储能 176MWh" />
          <KpiCard icon="📈" iconBg="#E8F5E9" label="理论发电量" value={fmEnergy(totalKpi?.gen_energy_mwh ?? 0)} unit="MWh" sub={null} />
          <KpiCard icon="☁️" iconBg="#F3E5F5" label="上网电量" value={fmEnergy(totalKpi?.grid_energy_mwh ?? 0)} unit="MWh" sub={`占发电量 ${totalKpi && totalKpi.gen_energy_mwh > 0 ? (totalKpi.grid_energy_mwh / totalKpi.gen_energy_mwh * 100).toFixed(1) : 0}%`} />
          <KpiCard icon="🔆" iconBg="#FFF3E0" label="利用小时数" value={totalKpi && totalKpi.gen_energy_mwh > 0 ? (totalKpi.grid_energy_mwh / 252).toFixed(0) : '0'} unit="h" sub={<span style={{ color: '#00A878' }}>等效满发时长</span>} />
          <KpiCard icon="⏱️" iconBg="#FCE4EC" label="限电率" value={format2(totalKpi?.curtailment_rate ?? 0)} unit="%" sub={`损失 ${fmEnergy((totalKpi?.gen_energy_mwh ?? 0) - (totalKpi?.grid_energy_mwh ?? 0))} MWh`} />
          <KpiCard icon="¥" iconBg="#E3F2FD" label="平均电价" value={format2(totalPrice)} unit="元/MWh" sub={<>标杆 {format2(kpiData?.benchmark_price)} 元/MWh</>} />
        </div>

        {/* Layer 3: Calendar left + Notices/Tasks right (tab switching) */}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '10px 12px', boxShadow: CARD_SHADOW }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: DARK, marginBottom: 6 }}>日历与事件</div>
            <CalendarWidget events={calendarEvents} />
          </div>
          <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '10px 12px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexShrink: 0 }}>
              {[
                { key: 'notices', label: '通知' },
                { key: 'tasks', label: '任务' },
              ].map(s => (
                <div
                  key={s.key}
                  onClick={() => setMobileRightTab(s.key)}
                  style={{
                    fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                    background: mobileRightTab === s.key ? '#E0F2F1' : '#F8FAFC',
                    color: mobileRightTab === s.key ? T : '#888',
                    fontWeight: mobileRightTab === s.key ? 600 : 400,
                  }}
                >
                  {s.label}
                </div>
              ))}
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {mobileRightTab === 'notices' ? (
                MOCK_NOTICES.map((n, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #F2F3F5', fontSize: 13 }}>
                    <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 11, marginRight: 8, whiteSpace: 'nowrap', fontWeight: 600, ...TAG_STYLE[n.cls] }}>{n.tag}</span>
                    <span style={{ color: '#333', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                    <span style={{ color: '#999', fontSize: 12, marginLeft: 6, whiteSpace: 'nowrap' }}>{n.date}</span>
                  </div>
                ))
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    {[
                      { key: 'all', label: `全部(${statusCounts.all})` },
                      { key: 'ing', label: `进行中(${statusCounts.ing || 0})` },
                      { key: 'wait', label: `待处理(${statusCounts.wait || 0})` },
                      { key: 'done', label: `已完成(${statusCounts.done || 0})` },
                    ].map(s2 => (
                      <div
                        key={s2.key}
                        onClick={() => setTaskFilter(s2.key)}
                        style={{
                          fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                          background: taskFilter === s2.key ? '#E0F2F1' : '#F8FAFC',
                          color: taskFilter === s2.key ? T : '#888',
                          fontWeight: taskFilter === s2.key ? 600 : 400,
                        }}
                      >
                        {s2.label}
                      </div>
                    ))}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '4px 8px', background: '#F8FAFC', color: '#5A6B8A', fontWeight: 600, borderBottom: '1px solid #EBEEF5', fontSize: 12 }}>任务名称</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', background: '#F8FAFC', color: '#5A6B8A', fontWeight: 600, borderBottom: '1px solid #EBEEF5', fontSize: 12 }}>负责人</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', background: '#F8FAFC', color: '#5A6B8A', fontWeight: 600, borderBottom: '1px solid #EBEEF5', fontSize: 12 }}>截止</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', background: '#F8FAFC', color: '#5A6B8A', fontWeight: 600, borderBottom: '1px solid #EBEEF5', fontSize: 12 }}>状态</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTasks.slice(0, 5).map((t, i) => {
                        const s = STATUS_MAP[t.status];
                        const isOverdue = dayjs(t.deadline).isBefore(dayjs(), 'day') && t.status !== 'done';
                        return (
                          <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #F2F3F5', fontWeight: 500, color: DARK }}>{t.name}</td>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #F2F3F5', color: '#333' }}>{t.owner}</td>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #F2F3F5', fontSize: 11, color: isOverdue ? '#E63946' : '#666', fontWeight: isOverdue ? 700 : 400 }}>
                              {t.deadline}{isOverdue ? ' (已超期)' : ''}
                            </td>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #F2F3F5' }}>
                              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, color: '#fff', background: { done: '#00A878', ing: T, wait: '#F4A261', delay: '#E63946' }[t.status] }}>
                                {s.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Layer 4: Charts 2+1 */}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '8px 10px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: DARK, marginBottom: 4 }}>上网电量趋势 - 按场站 (MWh)</div>
            <ReactECharts option={powerGridOption} style={{ height: 250 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '8px 10px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: DARK, marginBottom: 4 }}>日收益趋势 - 按场站（万元）</div>
            <ReactECharts option={revenueBarOption} style={{ height: 250 }} />
          </div>
        </div>
      </div>
    );
  }

  const dataLoading = isFetching;

  return (
    <div style={{ padding: '8px 12px', height: 'calc(100vh - 72px)', display: 'flex', flexDirection: 'column', gap: 8, overflow: 'hidden', background: '#F5F7FA' }}>
      {/* ── Time bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', borderRadius: 12, padding: '5px 12px', boxShadow: CARD_SHADOW, height: 36, flexShrink: 0 }}>
        <span style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>时间范围</span>
        <Segmented
          size="small"
          value={period}
          onChange={(v) => setPeriod(v as string)}
          options={PERIODS}
        />
        {weatherCities.length > 0 && (
          <>
            <style>{`
              @keyframes weatherCityScroll {
                0% { transform: translateY(0); }
                100% { transform: translateY(-${weatherCities.length * 20}px); }
              }
            `}</style>
            <div style={{
              height: 20, overflow: 'hidden', fontSize: 11, color: MUTED,
              lineHeight: '20px', whiteSpace: 'nowrap', flex: 1, marginRight: 12,
            }}>
              <div style={{
                animation: `weatherCityScroll ${weatherCities.length * 4}s linear infinite`,
              }}>
                {weatherCities.map((c, i) => (
                  <div key={i} style={{ height: 20 }}>
                    {c.city_name} 风速 {c.wind_speed} km/h
                  </div>
                ))}
                {/* duplicate for seamless loop */}
                {weatherCities.map((c, i) => (
                  <div key={`dup-${i}`} style={{ height: 20 }}>
                    {c.city_name} 风速 {c.wind_speed} km/h
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        <span style={{ fontSize: 12, color: MUTED, whiteSpace: 'nowrap' }}>
          {kpiData.date_start} ~ {kpiData.date_end}
        </span>
      </div>

      {/* ── Layer 1: Revenue + Chart + Todo (18%) ── */}
      <div style={{ display: 'flex', gap: 8, height: '18%', minHeight: 0, position: 'relative', opacity: dataLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {/* Revenue left */}
        <div style={{ flex: 0.9, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '10px 12px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>💰</div>
            <span style={{ fontSize: 14, color: '#333', fontWeight: 600 }}>收益（{PERIODS.find(p => p.value === period)?.label || ''}）</span>
          </div>
          <div style={{ fontSize: 36, fontWeight: 700, color: DARK, lineHeight: 1 }}>
            {format2(totalRev)}<span style={{ fontSize: 14, fontWeight: 400, color: MUTED, marginLeft: 4 }}>元</span>
          </div>
          <div style={{ display: 'flex', gap: 12, margin: '4px 0', fontSize: 12 }}>
            {totalKpi?.revenue_change_pct != null && (
              <span style={{ color: totalKpi.revenue_change_pct >= 0 ? '#00A878' : '#E63946', fontWeight: 600 }}>
                环比 {totalKpi.revenue_change_pct >= 0 ? '+' : ''}{totalKpi.revenue_change_pct}%
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#999', lineHeight: 1.6 }}>
            上网电量 {fmEnergy(totalEnergy)} MWh · 光伏 {fmEnergy(totalPvEnergy)} MWh<br />
            加权均价 {format2(totalPrice)} 元/MWh
          </div>
          {monthlyPlan && (
            <div style={{ marginTop: 'auto' }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
                本月发电计划{' '}
                <span style={{ color: T, fontWeight: 600 }}>
                  {(monthlyPlan.planned_gen_mwh > 0 ? (totalKpi?.gen_energy_mwh ?? 0) / monthlyPlan.planned_gen_mwh * 100 : 0).toFixed(0)}%
                </span>
                {' '}计划 {fmEnergy(monthlyPlan.planned_gen_mwh)} / 实际 {fmEnergy(totalKpi?.gen_energy_mwh ?? 0)} MWh
              </div>
              <div style={{ height: 6, background: GRID, borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                <div style={{ height: '100%', background: PLAN_COLOR, borderRadius: 3, width: `${Math.min(100, monthlyPlan.planned_gen_mwh > 0 ? (totalKpi?.gen_energy_mwh ?? 0) / monthlyPlan.planned_gen_mwh * 100 : 0)}%` }} />
              </div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
                本月收入计划{' '}
                <span style={{ color: T, fontWeight: 600 }}>
                  {(monthlyPlan.planned_revenue_yuan > 0 ? (totalKpi?.revenue_yuan ?? 0) / monthlyPlan.planned_revenue_yuan * 100 : 0).toFixed(0)}%
                </span>
                {' '}计划 {format2(monthlyPlan.planned_revenue_yuan)} / 实际 {format2(totalKpi?.revenue_yuan ?? 0)} 元
              </div>
              <div style={{ height: 6, background: GRID, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#f59e0b', borderRadius: 3, width: `${Math.min(100, monthlyPlan.planned_revenue_yuan > 0 ? (totalKpi?.revenue_yuan ?? 0) / monthlyPlan.planned_revenue_yuan * 100 : 0)}%` }} />
              </div>
            </div>
          )}
          {!monthlyPlan && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 'auto' }}>暂无本月计划数据</div>
          )}
        </div>

        {/* Revenue chart center */}
        <div style={{ flex: 1.4, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '8px 10px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: MUTED, marginBottom: 2 }}>收益趋势</div>
          <div style={{ flex: 1, minHeight: 0 }}>
            {breakdown.length > 0 ? <ReactECharts option={revenueChartOption} style={{ height: '100%' }} /> : <div style={{ color: '#999', textAlign: 'center', paddingTop: '15%' }}>暂无数据</div>}
          </div>
        </div>

        {/* Todo right */}
        <div style={{ flex: 0.7, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '10px 12px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#E63946', marginBottom: 6 }}>今日待办</div>
          {todayTodos.slice(0, 5).map((t, i) => {
            const pr = t.priority || 'normal';
            const tagBg = pr === 'urgent' ? '#FFEBEE' : pr === 'important' ? '#FFF8E1' : '#F5F5F5';
            const tagColor = pr === 'urgent' ? '#C62828' : pr === 'important' ? '#F57F17' : '#666';
            const cd = getCountdown(t.due_time || '');
            return (
              <div key={t.id || i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: '1px dashed #F2F3F5', fontSize: 11 }}>
                <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: tagBg, color: tagColor }}>{pr === 'urgent' ? '紧急' : pr === 'important' ? '重要' : '普通'}</span>
                <span style={{ flex: 1, color: '#333', textDecoration: cd.over ? 'line-through' : 'none' }}>{t.title}</span>
                {t.due_time && <span style={{ color: cd.over ? '#999' : '#E63946', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>{cd.over ? t.due_time : cd.text}</span>}
              </div>
            );
          })}
          {todayTodos.length === 0 && <div style={{ fontSize: 11, color: '#999', textAlign: 'center', padding: '20px 0' }}>暂无待办</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', fontSize: 11 }}>
            <span style={{ color: MUTED }}>今日共 {todayTodos.length} 项待办</span>
            <span style={{ color: T, cursor: 'pointer' }} onClick={() => window.location.href = '/todos'}>查看全部 →</span>
          </div>
        </div>
      </div>

      {/* ── Layer 2: KPI cards (14%) ── */}
      <div style={{ display: 'flex', gap: 8, height: '14%', minHeight: 0, opacity: dataLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <KpiCard icon="⚡" iconBg="#E3F2FD" label="总装机容量" value="252" unit="MW" sub="光伏 250MW / 储能 176MWh" />
        <KpiCard icon="📈" iconBg="#E8F5E9" label="理论发电量" value={fmEnergy(totalKpi?.gen_energy_mwh ?? 0)} unit="MWh" sub={null} />
        <KpiCard icon="☁️" iconBg="#F3E5F5" label="上网电量" value={fmEnergy(totalKpi?.grid_energy_mwh ?? 0)} unit="MWh" sub={`占发电量 ${totalKpi && totalKpi.gen_energy_mwh > 0 ? (totalKpi.grid_energy_mwh / totalKpi.gen_energy_mwh * 100).toFixed(1) : 0}%`} />
        <KpiCard icon="🔆" iconBg="#FFF3E0" label="利用小时数" value={totalKpi && totalKpi.gen_energy_mwh > 0 ? (totalKpi.grid_energy_mwh / 252).toFixed(0) : '0'} unit="h" sub={<span style={{ color: '#00A878' }}>等效满发时长</span>} />
        <KpiCard icon="⏱️" iconBg="#FCE4EC" label="限电率" value={format2(totalKpi?.curtailment_rate ?? 0)} unit="%" sub={`损失 ${fmEnergy((totalKpi?.gen_energy_mwh ?? 0) - (totalKpi?.grid_energy_mwh ?? 0))} MWh`} />
        <KpiCard icon="¥" iconBg="#E3F2FD" label="平均电价" value={format2(totalPrice)} unit="元/MWh" sub={<>标杆 {format2(kpiData?.benchmark_price)} 元/MWh</>} />
      </div>

      {/* ── Layer 3: Calendar + Notices + Tasks (22%) ── */}
      <div style={{ display: 'flex', gap: 8, height: '22%', minHeight: 0 }}>
        {/* Calendar */}
        <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '10px 12px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: DARK, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <span style={{ width: 3, height: 14, background: T, borderRadius: 2, display: 'inline-block' }} />
            日历与事件
          </div>
          <CalendarWidget events={calendarEvents} />
        </div>

        {/* Notices */}
        <div style={{ flex: 1.1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '10px 12px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexShrink: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: DARK, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 3, height: 14, background: T, borderRadius: 2, display: 'inline-block' }} />
              政策与通知
            </span>
            <span style={{ fontSize: 12, color: T, cursor: 'pointer' }}>查看更多 &gt;</span>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {MOCK_NOTICES.map((n, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #F2F3F5', fontSize: 13 }}>
                <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 11, marginRight: 8, whiteSpace: 'nowrap', fontWeight: 600, ...TAG_STYLE[n.cls] }}>{n.tag}</span>
                <span style={{ color: '#333', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                <span style={{ color: '#999', fontSize: 12, marginLeft: 6, whiteSpace: 'nowrap' }}>{n.date}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tasks */}
        <div style={{ flex: 1.2, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '10px 12px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexShrink: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: DARK, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 3, height: 14, background: T, borderRadius: 2, display: 'inline-block' }} />
              任务中心
            </span>
            <span style={{ fontSize: 12, color: T, cursor: 'pointer' }}>查看更多 &gt;</span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexShrink: 0 }}>
            {[
              { key: 'all', label: `全部(${statusCounts.all})` },
              { key: 'ing', label: `进行中(${statusCounts.ing || 0})` },
              { key: 'wait', label: `待处理(${statusCounts.wait || 0})` },
              { key: 'done', label: `已完成(${statusCounts.done || 0})` },
            ].map(s => (
              <div
                key={s.key}
                onClick={() => setTaskFilter(s.key)}
                style={{
                  fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                  background: taskFilter === s.key ? '#E0F2F1' : '#F8FAFC',
                  color: taskFilter === s.key ? T : '#888',
                  fontWeight: taskFilter === s.key ? 600 : 400,
                }}
              >
                {s.label}
              </div>
            ))}
          </div>
          <div style={{ flex: 1, overflow: 'auto', borderRadius: 6, border: '1px solid #EBEEF5' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px', background: '#F8FAFC', color: '#5A6B8A', fontWeight: 600, borderBottom: '1px solid #EBEEF5', fontSize: 12, position: 'sticky', top: 0 }}>任务名称</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', background: '#F8FAFC', color: '#5A6B8A', fontWeight: 600, borderBottom: '1px solid #EBEEF5', fontSize: 12, position: 'sticky', top: 0 }}>负责人</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', background: '#F8FAFC', color: '#5A6B8A', fontWeight: 600, borderBottom: '1px solid #EBEEF5', fontSize: 12, position: 'sticky', top: 0 }}>截止</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', background: '#F8FAFC', color: '#5A6B8A', fontWeight: 600, borderBottom: '1px solid #EBEEF5', fontSize: 12, position: 'sticky', top: 0 }}>状态</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.slice(0, 5).map((t, i) => {
                  const s = STATUS_MAP[t.status];
                  const isOverdue = dayjs(t.deadline).isBefore(dayjs(), 'day') && t.status !== 'done';
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #F2F3F5', fontWeight: 500, color: DARK }}>{t.name}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #F2F3F5', color: '#333' }}>{t.owner}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #F2F3F5', fontSize: 11, color: isOverdue ? '#E63946' : '#666', fontWeight: isOverdue ? 700 : 400 }}>
                        {t.deadline}{isOverdue ? ' (已超期)' : ''}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #F2F3F5' }}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, color: '#fff', background: { done: '#00A878', ing: T, wait: '#F4A261', delay: '#E63946' }[t.status] }}>
                          {s.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Layer 4: Charts (40%) ── */}
      <div style={{ display: 'flex', gap: 8, flex: 1, minHeight: 0, opacity: dataLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <div style={{ flex: 1.15, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '8px 10px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: DARK, marginBottom: 4, flexShrink: 0 }}>上网电量趋势 - 按场站 (MWh)</div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <ReactECharts option={powerGridOption} style={{ height: '100%' }} />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '8px 10px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: DARK, marginBottom: 4, flexShrink: 0 }}>日收益趋势 - 按场站（万元）</div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <ReactECharts option={revenueBarOption} style={{ height: '100%' }} />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: CARD_RADIUS, padding: '8px 10px', boxShadow: CARD_SHADOW, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: DARK, marginBottom: 4, flexShrink: 0 }}>全省电价曲线（日前 vs 实时）</div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <ReactECharts option={priceCurveOption} style={{ height: '100%' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
